import type { FC, ReactNode } from "react";

import { Button as AriaButton, type Key } from "react-aria-components";

import { Dropdown } from "@/components/ui/dropdown/dropdown-index";

export type ContextMenuAnchorPoint = {
	x: number;
	y: number;
};

type PointerContextMenuProps = {
	anchor: ContextMenuAnchorPoint | null;
	children: ReactNode;
	isOpen: boolean;
	onOpenChange: (isOpen: boolean) => void;
	/** Optional fixed width; defaults to Untitled UI `w-62` from Dropdown.Popover. */
	popoverClassName?: string;
};

/**
 * Dropdown menu positioned at a pointer location (right-click / long-press).
 *
 * Untitled UI context menus use `Dropdown.Root trigger="contextMenu"`, but our
 * React Aria version only supports press/longPress. This controlled variant
 * keeps the same Dropdown visuals and works for both DOM targets and Mapbox
 * layer hits where the menu must open at `clientX`/`clientY`.
 *
 * @see https://www.untitledui.com/react/components/context-menus
 */
export const PointerContextMenu = ({
	anchor,
	children,
	isOpen,
	onOpenChange,
	popoverClassName,
}: PointerContextMenuProps) => {
	if (!anchor) return null;

	return (
		<Dropdown.Root isOpen={isOpen} onOpenChange={onOpenChange}>
			<AriaButton
				aria-label="Context menu"
				className="pointer-events-none fixed size-px opacity-0"
				style={{ left: anchor.x, top: anchor.y }}
			/>
			<Dropdown.Popover className={popoverClassName} placement="bottom start">
				{children}
			</Dropdown.Popover>
		</Dropdown.Root>
	);
};

type PointerContextMenuItem = {
	icon?: FC<{ className?: string }>;
	id: Key;
	isDisabled?: boolean;
	label: string;
};

type SimplePointerContextMenuProps = {
	anchor: ContextMenuAnchorPoint | null;
	isOpen: boolean;
	items: PointerContextMenuItem[];
	onAction: (key: Key) => void;
	onOpenChange: (isOpen: boolean) => void;
	popoverClassName?: string;
};

/** Convenience wrapper with a standard Dropdown.Menu of labeled items. */
export const SimplePointerContextMenu = ({
	anchor,
	isOpen,
	items,
	onAction,
	onOpenChange,
	popoverClassName = "w-48",
}: SimplePointerContextMenuProps) => (
	<PointerContextMenu
		anchor={anchor}
		isOpen={isOpen}
		onOpenChange={onOpenChange}
		popoverClassName={popoverClassName}
	>
		<Dropdown.Menu
			onAction={(key) => {
				onAction(key);
				onOpenChange(false);
			}}
		>
			{items.map((item) => (
				<Dropdown.Item
					icon={item.icon}
					id={item.id}
					isDisabled={item.isDisabled}
					key={item.id}
					label={item.label}
				/>
			))}
		</Dropdown.Menu>
	</PointerContextMenu>
);
